import React, { useMemo, useState } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Autocomplete,
    Tabs,
    Tab,
    TablePagination,
    Avatar,
    Tooltip,
    Collapse,
    Typography,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import useAuthStore from "@/store/useAuthStore";
import { useTenants, useCreditUsage, useUsers } from "@/hooks/useTenantDashboardService";
import type { CreditConsumption, UsageReportResponse, CreditBalance, CreditGrant } from "@/types/creditTypes";
import { formatDateUS } from "@/utils/dateUtils";
import { renderSinglePlusMore } from "../renderSinglePlusMore";

const DATE_DISPLAY_FORMAT = "MM/DD/YYYY";
const DATE_API_FORMAT = "YYYY-MM-DD";
const AZURE_GPT_INPUT_COST = 0.000005; // $5.00 per 1M tokens
const AZURE_GPT_OUTPUT_COST = 0.000015; // $15.00 per 1M tokens
// The 5 trackable features across the platform — keys must match the
// `feature` string stored on each CreditConsumption row.
const FEATURE_COLUMNS: { key: string; label: string; accent: string }[] = [
    { key: "practice_session", label: "Practice Session", accent: "#818CF8" },
    { key: "pitches", label: "Pitches", accent: "#F472B6" },
    { key: "digital_twin_session", label: "Digital Twin", accent: "#38BDF8" },
    { key: "meetings", label: "Meetings", accent: "#FBBF24" },
    { key: "quick-pitch", label: "Quick Pitch", accent: "#A78BFA" },
    { key: "allocations", label: "Allocations", accent: "#34D399" },
];

const normalizeFeatureKey = (rawFeature?: string): string => {
    if (!rawFeature) return "other";
    const feat = rawFeature.trim().toLowerCase().replace(/[-\s]+/g, "_");

    if (feat.includes("practice")) {
        return "practice_session";
    }
    if (feat.includes("quick")) {
        return "quick-pitch";
    }
    if (
        feat.includes("digital_twin") ||
        feat.includes("digitaltwin") ||
        feat.includes("twin")
    ) {
        return "digital_twin_session";
    }
    if (feat.includes("meeting")) {
        return "meetings";
    }
    if (feat.includes("pitch") || feat.includes("script")) {
        return "pitches";
    }
    if (
        feat.includes("distribution") ||
        feat.includes("allocation") ||
        feat.includes("grant")
    ) {
        return "allocations";
    }

    return feat;
};

interface GroupedUserUsage {
    userId: string;
    userName: string;
    userEmail?: string;
    tenantId?: string;
    featureTotals: Record<string, number>;
    totalCredits: number;
    featureTokens: Record<string, number>;
    totalTokens: number;
    featureInputTokens: Record<string, number>;
    featureOutputTokens: Record<string, number>;
    inputTokens: number;
    outputTokens: number;
    apiCalls: number;
    featureApiCalls: Record<string, number>;
}

const inputSx = {
    height: "44px",
    color: "var(--color-text)",
    backgroundColor: "var(--color-surface)",
    borderRadius: "9999px",
    paddingRight: "30px",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--color-border)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--color-stat-accent)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--color-stat-accent)",
    },
    "& input": {
        color: "var(--color-text)",
        padding: "0 14px",
        height: "44px",
        boxSizing: "border-box",
    },
    "& .MuiSvgIcon-root": {
        color: "var(--color-text)",
    },
};

// Deterministic pastel-on-dark color for a user avatar, based on their id.
const getAvatarColor = (seed: string) => {
    const palette = ["#B8F890", "#818CF8", "#F472B6", "#38BDF8", "#FBBF24", "#A78BFA", "#34D399"];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
};

const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || name[0]?.toUpperCase();
};

const formatNumber = (num: number) =>
    new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(num);

const calculateCost = (input: number, output: number) => {
    return (input * AZURE_GPT_INPUT_COST) + (output * AZURE_GPT_OUTPUT_COST);
};

const UsageRow = ({ row, tenantName, isSuperAdmin }: { row: GroupedUserUsage; tenantName: string; isSuperAdmin: boolean }) => {
    const [open, setOpen] = useState(false);
    const avatarColor = getAvatarColor(row.userId);

    const totalCost = calculateCost(row.inputTokens, row.outputTokens);

    return (
        <React.Fragment>
            <TableRow hover sx={{ "& > *": { borderBottom: "unset" }, "&:hover": { backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 5%, transparent) !important" } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} sx={{ color: "var(--color-text)" }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <div className="flex items-center gap-2">
                        <Avatar
                            sx={{
                                width: 30,
                                height: 30,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: `${avatarColor}22`,
                                color: avatarColor,
                                border: `1px solid ${avatarColor}55`,
                            }}
                        >
                            {getInitials(row.userName)}
                        </Avatar>
                        <div>
                            <div className="text-sm font-medium text-[var(--color-text)] leading-tight">{row.userName}</div>
                            {row.userEmail && (
                                <div className="text-[11px] text-gray-500 leading-tight">{row.userEmail}</div>
                            )}
                        </div>
                    </div>
                </TableCell>
                {isSuperAdmin && (
                    <TableCell sx={{ color: "var(--color-muted)" }}>{tenantName}</TableCell>
                )}
                <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600 }}>{row.totalTokens.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600 }}>{row.inputTokens.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600 }}>{row.outputTokens.toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ color: "var(--color-stat-accent)", fontWeight: 600 }}>${totalCost.toFixed(4)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: open ? '1px solid var(--color-border)' : 'none' }} colSpan={isSuperAdmin ? 7 : 7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, backgroundColor: "var(--color-bg)", borderRadius: 2, overflow: "hidden", border: "1px solid var(--color-border)" }}>
                            <Typography variant="subtitle2" gutterBottom component="div" sx={{ p: 2, backgroundColor: "var(--color-table-header)", color: "var(--color-text)", fontWeight: "bold" }}>
                                Feature Breakdown
                            </Typography>
                            <Table size="small" aria-label="features">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "var(--color-text)", fontWeight: "bold" }}>Feature</TableCell>
                                        <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: "bold" }}>Total Tokens</TableCell>
                                        <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: "bold" }}>Input Tokens</TableCell>
                                        <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: "bold" }}>Output Tokens</TableCell>
                                        <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: "bold" }}>Cost (USD)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {FEATURE_COLUMNS.map((col) => {
                                        const featureTotalTokens = row.featureTokens[col.key] || 0;
                                        if (featureTotalTokens === 0) return null;
                                        const featureInputTokens = row.featureInputTokens[col.key] || 0;
                                        const featureOutputTokens = row.featureOutputTokens[col.key] || 0;
                                        const featureCost = calculateCost(featureInputTokens, featureOutputTokens);

                                        return (
                                            <TableRow key={col.key}>
                                                <TableCell component="th" scope="row" sx={{ color: "var(--color-muted)" }}>{col.label}</TableCell>
                                                <TableCell align="right" sx={{ color: "var(--color-text)" }}>{featureTotalTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right" sx={{ color: "var(--color-text)" }}>{featureInputTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right" sx={{ color: "var(--color-text)" }}>{featureOutputTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right" sx={{ color: "var(--color-text)" }}>${featureCost.toFixed(4)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const CreditUsageTable: React.FC = () => {
    const { role, tenantid: localTenantId } = useAuthStore();
    const isSuperAdmin = role === "SUPER_ADMIN";

    const [selectedTenantId, setSelectedTenantId] = useState("");
    const [startDate, setStartDate] = useState(dayjs().subtract(8, "month").format(DATE_DISPLAY_FORMAT));
    const [endDate, setEndDate] = useState(dayjs().format(DATE_DISPLAY_FORMAT));
    const [selectedUserIds, setSelectedUserIds] = useState<any>([]);
    const [selectedSource, setSelectedSource] = useState("");
    const [currentTab, setCurrentTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [pageByTab, setPageByTab] = useState([0, 0, 0, 0]);
    const [rowsPerPageByTab, setRowsPerPageByTab] = useState([10, 10, 10, 10]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const { data: tenants } = useTenants();

    const effectiveTenantId = isSuperAdmin ? selectedTenantId : localTenantId || "";

    const { data: userData } = useUsers({
        currentAuthRole: role,
        role: "",
        pageSize: 1000,
        tenantId: effectiveTenantId || undefined,
    });

    // Extract sorted list of tenant options
    const tenantOptions = useMemo(() => {
        const rawList = tenants?.data || [];
        const list: { id: string; name: string }[] = [];
        rawList.forEach((item: any) => {
            const id = item?.tenant?.id || item?.id;
            const name = item?.tenant?.name || item?.name;
            if (id && name) {
                list.push({ id: String(id), name: String(name) });
            }
        });
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [tenants]);

    const tenantDropdownOptions = useMemo(() => {
        return [{ id: "", name: "All Tenants" }, ...tenantOptions];
    }, [tenantOptions]);

    const getTenantName = (tId?: string | null) => {
        if (!tId) return "—";
        return tenantOptions.find((t) => t.id === tId)?.name || tId;
    };

    // React Query Hook
    const { data: creditUsageData, isLoading } = useCreditUsage(
        {
            tenantId: effectiveTenantId,
            startDate: startDate ? dayjs(startDate, DATE_DISPLAY_FORMAT).format(DATE_API_FORMAT) : undefined,
            endDate: endDate ? dayjs(endDate, DATE_DISPLAY_FORMAT).format(DATE_API_FORMAT) : undefined,
        },
        true
    );

    // Cast response to new type
    const { consumptions = [], grants = [], balances = [] } = (creditUsageData as unknown as UsageReportResponse) || {};

    // Combine users from API + usage data so users dropdown is always populated
    const usersOptions = useMemo(() => {
        const userMap = new Map<string, { id: string; name: string; email?: string }>();

        // 1. From API users
        const apiUsers = userData?.data?.items ?? [];
        apiUsers.forEach((u: any) => {
            if (u && u.id) {
                if (!effectiveTenantId || u.tenantId === effectiveTenantId) {
                    userMap.set(String(u.id), {
                        id: String(u.id),
                        name: u.name || u.email || "Unknown",
                        email: u.email || "",
                    });
                }
            }
        });

        // 2. From balances
        balances.forEach((bal: CreditBalance) => {
            if (bal.User?.id) {
                const uId = String(bal.User.id);
                if (!userMap.has(uId)) {
                    userMap.set(uId, {
                        id: uId,
                        name: bal.User.name || bal.User.email || "Unknown",
                        email: bal.User.email || "",
                    });
                }
            }
        });

        // 3. From consumptions
        consumptions.forEach((c: CreditConsumption) => {
            if (c.userId) {
                const uId = String(c.userId);
                if (!userMap.has(uId)) {
                    userMap.set(uId, {
                        id: uId,
                        name: c.User?.name || c.User?.email || "Unknown",
                        email: c.User?.email || "",
                    });
                }
            }
        });

        // 4. From grants
        grants.forEach((g: CreditGrant) => {
            if (g.CreditBalance?.User?.id) {
                const uId = String(g.CreditBalance.User.id);
                if (!userMap.has(uId)) {
                    userMap.set(uId, {
                        id: uId,
                        name: g.CreditBalance.User.name || g.CreditBalance.User.email || "Unknown",
                        email: g.CreditBalance.User.email || "",
                    });
                }
            }
        });

        return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [userData, effectiveTenantId, balances, consumptions, grants]);

    const normalizedQuery = searchQuery.trim().toLowerCase();

    // Client-side filtering for consumers (if API doesn't support it or for UI refinement)
    const filteredConsumptions = useMemo(() => {
        const base = consumptions.filter((log: CreditConsumption) => {
            if (selectedUserIds.length === 0) return true;
            return selectedUserIds.some((u: any) => u.id === log.userId || (log.relatedObjectId && u.id === log.relatedObjectId));
        });

        if (!normalizedQuery) return base;
        return base.filter((log: CreditConsumption) => {
            const normalizedFeature = normalizeFeatureKey(log.feature);
            const isAllocation = normalizedFeature === "allocations";
            const recipientUser = isAllocation && log.relatedObjectId ? usersOptions.find((u) => u.id === log.relatedObjectId) : null;
            const tenantName = getTenantName(log.User?.tenantId);
            const haystack = [
                log.User?.name,
                log.User?.email,
                recipientUser?.name,
                recipientUser?.email,
                tenantName,
                log.feature,
                log.CreditGrant?.source,
                String(log.creditsUsed ?? ""),
                formatDateUS(log.consumedAt, true),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [consumptions, selectedUserIds, normalizedQuery, tenantOptions, usersOptions]);

    // Map of refunds by user, so if an allocation was partially or fully revoked, the net allocation is shown
    const userRefunds = useMemo(() => {
        const refundsByUser = new Map<string, number>();

        grants.forEach((g: CreditGrant) => {
            if (g.source === "COMPANY_ADMIN_REFUND") {
                let targetUserId = "";

                // 1. Check if stripeEventId contains a grantId (e.g. company_admin_refund_<grantId>_<timestamp>)
                if (g.stripeEventId && g.stripeEventId.startsWith("company_admin_refund_")) {
                    const parts = g.stripeEventId.split("_");
                    const grantId = parts[3];
                    if (grantId) {
                        const targetGrant = grants.find((orig) => orig.id === grantId);
                        if (targetGrant?.CreditBalance?.User?.id) {
                            targetUserId = targetGrant.CreditBalance.User.id;
                        }
                    }
                }

                // 2. Fallback: check if referenceId matches a user
                if (!targetUserId && g.referenceId) {
                    const u = usersOptions.find((uo) => uo.id === g.referenceId);
                    if (u) {
                        targetUserId = u.id;
                    }
                }

                if (targetUserId) {
                    refundsByUser.set(targetUserId, (refundsByUser.get(targetUserId) || 0) + (g.credits || 0));
                }
            }
        });

        return refundsByUser;
    }, [grants, usersOptions]);

    // Map of refunds by original grantId so Grant History reflects original granted amount before refund/reduction
    const refundsByGrantId = useMemo(() => {
        const map = new Map<string, number>();
        const prefix = "company_admin_refund_";

        grants.forEach((g: CreditGrant) => {
            if (g.source === "COMPANY_ADMIN_REFUND" && g.stripeEventId && g.stripeEventId.startsWith(prefix)) {
                const withoutPrefix = g.stripeEventId.slice(prefix.length);
                const lastUnderscore = withoutPrefix.lastIndexOf("_");
                const grantId = lastUnderscore !== -1 ? withoutPrefix.slice(0, lastUnderscore) : withoutPrefix;
                if (grantId) {
                    map.set(grantId, (map.get(grantId) || 0) + (g.credits || 0));
                }
            }
        });

        return map;
    }, [grants]);

    // Map of refund source user name by the refund grant's id
    const refundSourceUserByGrantId = useMemo(() => {
        const map = new Map<string, string>();
        const prefix = "company_admin_refund_";

        grants.forEach((g: CreditGrant) => {
            if (g.source === "COMPANY_ADMIN_REFUND") {
                let name = "";

                // 1. Resolve from stripeEventId -> original grant's User
                if (g.stripeEventId && g.stripeEventId.startsWith(prefix)) {
                    const withoutPrefix = g.stripeEventId.slice(prefix.length);
                    const lastUnderscore = withoutPrefix.lastIndexOf("_");
                    const origGrantId = lastUnderscore !== -1 ? withoutPrefix.slice(0, lastUnderscore) : withoutPrefix;
                    if (origGrantId) {
                        const origGrant = grants.find((og: CreditGrant) => og.id === origGrantId);
                        name = origGrant?.CreditBalance?.User?.name || origGrant?.CreditBalance?.User?.email || "";
                    }
                }

                // 2. Fallback: resolve from referenceId
                if (!name && g.referenceId) {
                    const u = usersOptions.find((uo) => uo.id === g.referenceId);
                    name = u?.name || u?.email || "";
                }

                if (name) {
                    map.set(g.id, name);
                }
            }
        });

        return map;
    }, [grants, usersOptions]);

    // Map of fallback expiry date for refund grants (inherits company pool / original grant expiry)
    const refundExpiryByGrantId = useMemo(() => {
        const map = new Map<string, string>();
        const prefix = "company_admin_refund_";

        grants.forEach((g: CreditGrant) => {
            if (g.source === "COMPANY_ADMIN_REFUND") {
                if (g.expiresAt) {
                    map.set(g.id, g.expiresAt);
                    return;
                }

                // Try finding expiry from original grant
                if (g.stripeEventId && g.stripeEventId.startsWith(prefix)) {
                    const withoutPrefix = g.stripeEventId.slice(prefix.length);
                    const lastUnderscore = withoutPrefix.lastIndexOf("_");
                    const origGrantId = lastUnderscore !== -1 ? withoutPrefix.slice(0, lastUnderscore) : withoutPrefix;
                    if (origGrantId) {
                        const origGrant = grants.find((og: CreditGrant) => og.id === origGrantId);
                        if (origGrant?.expiresAt) {
                            map.set(g.id, origGrant.expiresAt);
                            return;
                        }
                    }
                }

                // Fallback: check tenant pool grants
                const poolGrant = grants.find(
                    (pg: CreditGrant) =>
                        pg.source === "SUPERADMIN_GRANT" &&
                        pg.expiresAt
                );
                if (poolGrant?.expiresAt) {
                    map.set(g.id, poolGrant.expiresAt);
                }
            }
        });

        return map;
    }, [grants]);




    // Group every consumption row by user, summing credits + tokens per feature
    // plus overall totals — this powers both the "Usage Logs" and "Token Usage" tabs.
    const groupedUsage: GroupedUserUsage[] = useMemo(() => {
        const map = new Map<string, GroupedUserUsage>();

        filteredConsumptions.forEach((log: CreditConsumption) => {
            const normalizedFeature = normalizeFeatureKey(log.feature);
            const isAllocation = normalizedFeature === "allocations";
            const recipientUser = isAllocation && log.relatedObjectId ? usersOptions.find((u) => u.id === log.relatedObjectId) : null;

            const userId = recipientUser ? recipientUser.id : log.userId || "unknown";
            const userName = recipientUser ? recipientUser.name : log.User?.name || log.User?.email || "Unknown";
            const userEmail = recipientUser ? recipientUser.email : log.User?.email;
            const tenantId = log.User?.tenantId;

            if (!map.has(userId)) {
                map.set(userId, {
                    userId,
                    userName,
                    userEmail,
                    tenantId,
                    featureTotals: {},
                    featureTokens: {},
                    featureInputTokens: {},
                    featureOutputTokens: {},
                    featureApiCalls: {},
                    totalCredits: 0,
                    totalTokens: 0,
                    inputTokens: 0,
                    outputTokens: 0,
                    apiCalls: 0,
                });
            }
            const entry = map.get(userId)!;
            const used = log.creditsUsed ?? 0;
            
            const metrics = (log as any).aiMetrics || {};
            const tokens = metrics.aiTotalTokens ?? 0;
            const promptTokens = metrics.aiPromptTokens ?? 0;
            const completionTokens = metrics.aiCompletionTokens ?? 0;

            entry.featureTotals[normalizedFeature] = (entry.featureTotals[normalizedFeature] || 0) + used;
            entry.featureTokens[normalizedFeature] = (entry.featureTokens[normalizedFeature] || 0) + tokens;
            entry.featureInputTokens[normalizedFeature] = (entry.featureInputTokens[normalizedFeature] || 0) + promptTokens;
            entry.featureOutputTokens[normalizedFeature] = (entry.featureOutputTokens[normalizedFeature] || 0) + completionTokens;
            entry.featureApiCalls[normalizedFeature] = (entry.featureApiCalls[normalizedFeature] || 0) + 1;
            entry.apiCalls += 1;
        });

        // Deduct refunds from allocations so the net allocation is shown
        map.forEach((entry, uId) => {
            const refund = userRefunds.get(uId) || 0;
            if (refund > 0 && entry.featureTotals["allocations"]) {
                entry.featureTotals["allocations"] = Math.max(0, entry.featureTotals["allocations"] - refund);
            }
        });

        const list = Array.from(map.values()).map((entry) => {
            // Total credits is strictly the sum of all session column totals for this user
            const totalCredits = FEATURE_COLUMNS.reduce(
                (sum, col) => sum + (entry.featureTotals[col.key] || 0),
                0
            );
            const totalTokens = FEATURE_COLUMNS.reduce(
                (sum, col) => sum + (entry.featureTokens[col.key] || 0),
                0
            );
            const inputTokens = FEATURE_COLUMNS.reduce(
                (sum, col) => sum + (entry.featureInputTokens[col.key] || 0),
                0
            );
            const outputTokens = FEATURE_COLUMNS.reduce(
                (sum, col) => sum + (entry.featureOutputTokens[col.key] || 0),
                0
            );
            return {
                ...entry,
                totalCredits,
                totalTokens,
                inputTokens,
                outputTokens,
            };
        });

        return list.sort((a, b) => b.totalCredits - a.totalCredits);
    }, [filteredConsumptions, usersOptions, userRefunds]);

    const filteredBalances = useMemo(() => {
        let list = balances;

        if (selectedUserIds.length > 0) {
            list = list.filter((bal: CreditBalance) => {
                const userId = bal.User?.id;
                return selectedUserIds.some((u: any) => u.id === userId);
            });
        }

        if (!normalizedQuery) return list;

        return list.filter((bal: CreditBalance) => {
            const tenantName = bal.Tenant?.name || getTenantName(bal.tenantId);
            const haystack = [
                bal.User?.name,
                bal.User?.email,
                tenantName,
                String(bal.balance ?? ""),
                String(bal.dailyBalance ?? ""),
                formatDateUS(bal.updatedAt, true),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [balances, selectedUserIds, normalizedQuery, tenantOptions]);

    const sourceOptions = useMemo(() => {
        const set = new Set<string>();
        grants.forEach((grant: CreditGrant) => {
            if (grant.source) {
                set.add(grant.source);
            }
        });
        return Array.from(set).sort();
    }, [grants]);

    const filteredGrants = useMemo(() => {
        let list = grants;

        if (selectedSource) {
            list = list.filter((grant: CreditGrant) => grant.source === selectedSource);
        }

        if (selectedUserIds.length > 0) {
            list = list.filter((grant: CreditGrant) => {
                const userId = grant.CreditBalance?.User?.id;
                return selectedUserIds.some((u: any) => u.id === userId);
            });
        }

        if (!normalizedQuery) return list;

        return list.filter((grant: CreditGrant) => {
            const refundAmount = refundsByGrantId.get(grant.id) || 0;
            const displayCredits = grant.credits + refundAmount;
            const refundSourceUser = refundSourceUserByGrantId.get(grant.id);
            const effectiveExpiresAt = grant.expiresAt || refundExpiryByGrantId.get(grant.id);
            const recipient =
                grant.CreditBalance?.User?.name ||
                grant.CreditBalance?.User?.email ||
                grant.CreditBalance?.Tenant?.name ||
                getTenantName(grant.CreditBalance?.tenantId || grant.CreditBalance?.Tenant?.id || grant.CreditBalance?.User?.tenantId) ||
                "";
            const haystack = [
                recipient,
                grant.source,
                grant.source === "SUPERADMIN_GRANT"
                    ? "Superadmin Grant"
                    : grant.source === "COMPANY_ADMIN_GRANT" || grant.source === "TENANT_ADMIN_GRANT"
                    ? "Company Admin Grant"
                    : grant.source === "COMPANY_ADMIN_REFUND"
                    ? "Company Admin Refund"
                    : grant.source,
                refundSourceUser,
                refundSourceUser ? `refunded from ${refundSourceUser}` : "",
                String(grant.credits ?? ""),
                String(displayCredits),
                formatDateUS(grant.grantedAt, true),
                effectiveExpiresAt ? formatDateUS(effectiveExpiresAt) : "Never",
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(normalizedQuery);
        });
    }, [grants, selectedSource, selectedUserIds, normalizedQuery, tenantOptions, refundsByGrantId, refundSourceUserByGrantId, refundExpiryByGrantId]);

    const paginate = <T,>(items: T[], tabIndex: number) => {
        const page = pageByTab[tabIndex] ?? 0;
        const rowsPerPage = rowsPerPageByTab[tabIndex] ?? 10;
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return items.slice(start, end);
    };

    const handlePageChange = (_: unknown, newPage: number) => {
        setPageByTab((prev) => {
            const next = [...prev];
            next[currentTab] = newPage;
            return next;
        });
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        setRowsPerPageByTab((prev) => {
            const next = [...prev];
            next[currentTab] = value;
            return next;
        });
        setPageByTab((prev) => {
            const next = [...prev];
            next[currentTab] = 0;
            return next;
        });
    };

    return (
        <div className="p-2 rounded-2xl overflow-x-auto text-white border border-[#2A2636] bg-[#1B1926] shadow-[0_12px_32px_rgba(0,0,0,0.25)] credit-usage-container">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                    <h2 className="text-2xl font-semibold">Credit Usage</h2>
                    <p className="text-xs text-[#9CA3AF] mt-1">Usage, balances, and grants in one place.</p>
                </div>
                <TextField
                    label="Search"
                    placeholder="Search user, tenant, feature..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPageByTab([0, 0, 0, 0]);
                    }}
                    variant="outlined"
                    InputLabelProps={{ sx: { color: "var(--color-text)" } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "var(--color-muted)", fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setSearchQuery("");
                                        setPageByTab([0, 0, 0, 0]);
                                    }}
                                    sx={{ color: "var(--color-muted)" }}
                                >
                                    <ClearIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        minWidth: 240,
                        flex: "0 1 320px",
                        "& .MuiOutlinedInput-root": {
                            ...inputSx,
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--color-border)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--color-stat-accent)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                border: "2px solid var(--color-stat-accent)",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: "var(--color-text)",
                            "&.Mui-focused": {
                                color: "var(--color-stat-accent)",
                            },
                        },
                    }}
                />
            </div>

            {/* Filters */}
            <Box
                display="flex"
                gap={2}
                flexWrap="wrap"
                mb={3}
                mt={2}
                p={2}
                className="credit-usage-card"
                sx={{ backgroundColor: "var(--color-surface)", borderRadius: 2, border: "1px solid var(--color-border)" }}
            >
                {isSuperAdmin && (
                    <Autocomplete
                        options={tenantDropdownOptions}
                        getOptionLabel={(option: { id: string; name: string }) => option?.name || ""}
                        value={tenantDropdownOptions.find((t) => t.id === selectedTenantId) || tenantDropdownOptions[0]}
                        onChange={(_, newValue) => {
                            setSelectedTenantId(newValue ? newValue.id : "");
                            setSelectedUserIds([]); // Reset users when tenant changes
                            setPageByTab([0, 0, 0, 0]);
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        slotProps={{
                            paper: {
                                sx: {
                                    backgroundColor: "var(--color-surface)",
                                    color: "var(--color-text)",
                                    border: "1px solid var(--color-border)",
                                },
                            },
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id} style={{ color: "var(--color-text)" }}>
                                {option.name}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tenant"
                                placeholder={selectedTenantId ? "" : "Search tenant..."}
                                variant="outlined"
                                InputLabelProps={{ sx: { color: "var(--color-text)" } }}
                                sx={{
                                    minWidth: 200,
                                    flex: "1 1 220px",
                                    "& .MuiOutlinedInput-root": {
                                        ...inputSx,
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: selectedTenantId ? "2px solid var(--color-stat-accent)" : "1px solid var(--color-border)",
                                        },
                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                            border: "2px solid var(--color-stat-accent)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            border: "2px solid var(--color-stat-accent)",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "var(--color-text)",
                                        "&.Mui-focused": { color: "var(--color-stat-accent)" },
                                    },
                                    "& .MuiAutocomplete-popupIndicator": {
                                        color: "var(--color-stat-accent)",
                                    },
                                    "& .MuiAutocomplete-clearIndicator": {
                                        color: "var(--color-stat-accent)",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "var(--color-stat-accent)",
                                    },
                                }}
                            />
                        )}
                    />
                )}

                <Autocomplete
                    multiple
                    options={usersOptions}
                    getOptionLabel={(opt: any) => {
                        if (!opt) return "";
                        if (opt.name && opt.email && opt.name !== opt.email) {
                            return `${opt.name} (${opt.email})`;
                        }
                        return opt.name || opt.email || "Unknown";
                    }}
                    value={usersOptions.filter((opt) => selectedUserIds.some((u: any) => u.id === opt.id))}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={(_, newVals) => {
                        setSelectedUserIds(
                            newVals.map((v: any) => ({ id: String(v.id), name: v.name || v.email })),
                        );
                        setPageByTab([0, 0, 0, 0]);
                    }}
                    renderOption={(props, option: any) => (
                        <li {...props} key={option.id}>
                            <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: "var(--color-text)" }}>
                                    {option.name || "Unknown"}
                                </Typography>
                                {option.email && option.name !== option.email && (
                                    <Typography variant="caption" sx={{ color: "var(--color-muted, #9CA3AF)", opacity: 0.8 }}>
                                        {option.email}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                    renderTags={renderSinglePlusMore}
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: "var(--color-surface)",
                                color: "var(--color-text)",
                                border: "1px solid var(--color-border)",
                            },
                        },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Users"
                            placeholder={selectedUserIds.length === 0 ? "Search users..." : ""}
                            variant="outlined"
                            InputLabelProps={{ sx: { color: "var(--color-text)" } }}
                            sx={{
                                minWidth: 200,
                                flex: "1 1 220px",
                                "& .MuiOutlinedInput-root": {
                                    ...inputSx,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: selectedUserIds.length > 0 ? "2px solid var(--color-stat-accent)" : "1px solid var(--color-border)",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        border: "2px solid var(--color-stat-accent)",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        border: "2px solid var(--color-stat-accent)",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "var(--color-text)",
                                    "&.Mui-focused": {
                                        color: "var(--color-stat-accent)",
                                    },
                                },
                                "& .MuiAutocomplete-popupIndicator": {
                                    color: "var(--color-stat-accent)",
                                },
                                "& .MuiAutocomplete-clearIndicator": {
                                    color: "var(--color-stat-accent)",
                                },
                                "& .MuiSvgIcon-root": {
                                    color: "var(--color-stat-accent)",
                                },
                                "& .MuiAutocomplete-tag": {
                                    backgroundColor: "var(--color-stat-accent)",
                                    color: "black",
                                    fontSize: "0.75rem",
                                    padding: "0 8px",
                                    borderRadius: "9999px",
                                },
                            }}
                        />
                    )}
                />

                {currentTab === 2 && (
                    <TextField
                        select
                        label="Source"
                        value={selectedSource}
                        onChange={(e) => {
                            setSelectedSource(e.target.value);
                            setPageByTab([0, 0, 0, 0]);
                        }}
                        variant="outlined"
                        InputLabelProps={{ sx: { color: "var(--color-text)" } }}
                        SelectProps={{
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        backgroundColor: "var(--color-surface)",
                                        color: "var(--color-text)",
                                        border: "1px solid var(--color-border)",
                                    },
                                },
                            },
                        }}
                        sx={{
                            minWidth: 180,
                            flex: "1 1 180px",
                            "& .MuiOutlinedInput-root": {
                                ...inputSx,
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: selectedSource ? "2px solid var(--color-stat-accent)" : "1px solid var(--color-border)",
                                },
                            },
                            "& .MuiInputLabel-root": {
                                color: "var(--color-text)",
                                "&.Mui-focused": { color: "var(--color-stat-accent)" },
                            },
                            "& .MuiSelect-icon": { color: "var(--color-stat-accent)" },
                            "& .MuiOutlinedInput-input": {
                                color: "var(--color-text)",
                                display: "flex",
                                alignItems: "center",
                                paddingTop: "0 !important",
                                paddingBottom: "0 !important",
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>All Sources</em>
                        </MenuItem>
                        {sourceOptions.map((src) => (
                            <MenuItem key={src} value={src}>
                                {src === "SUPERADMIN_GRANT"
                                    ? "Superadmin Grant"
                                    : src.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Start Date"
                        value={dayjs(startDate, DATE_DISPLAY_FORMAT)}
                        format={DATE_DISPLAY_FORMAT}
                        onChange={(newValue) => setStartDate(newValue?.format(DATE_DISPLAY_FORMAT) || "")}
                        slots={{ openPickerIcon: () => <CalendarMonthIcon sx={{ color: "var(--color-text)" }} /> }}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                InputLabelProps: { shrink: true, sx: { color: "var(--color-text)" } },
                                InputProps: { sx: inputSx },
                                sx: { minWidth: 140, flex: "1 1 130px", "& label": { color: "var(--color-text)", "&.Mui-focused": { color: "var(--color-stat-accent)" } } }
                            }
                        }}
                    />
                    <DatePicker
                        label="End Date"
                        value={dayjs(endDate, DATE_DISPLAY_FORMAT)}
                        format={DATE_DISPLAY_FORMAT}
                        onChange={(newValue) => setEndDate(newValue?.format(DATE_DISPLAY_FORMAT) || "")}
                        slots={{ openPickerIcon: () => <CalendarMonthIcon sx={{ color: "var(--color-text)" }} /> }}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                InputLabelProps: { shrink: true, sx: { color: "var(--color-text)" } },
                                InputProps: { sx: inputSx },
                                sx: { minWidth: 140, flex: "1 1 130px", "& label": { color: "var(--color-text)", "&.Mui-focused": { color: "var(--color-stat-accent)" } } }
                            }
                        }}
                    />
                </LocalizationProvider>
            </Box>

            {/* Content Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'var(--color-border)', mb: 3 }}>
                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    textColor="secondary"
                    indicatorColor="secondary"
                    sx={{
                        '& .MuiTab-root': { color: 'var(--color-muted)', textTransform: 'none', fontSize: '0.95rem', fontWeight: 500 },
                        '& .Mui-selected': { color: 'var(--color-stat-accent) !important' },
                        '& .MuiTabs-indicator': { backgroundColor: 'var(--color-stat-accent)' }
                    }}
                >
                    <Tab label="Usage Logs" />
                    <Tab label="Active Balances" />
                    <Tab label="Grant History" />
                    {isSuperAdmin && <Tab label="Token Usage" />}
                </Tabs>
            </Box>

            {/* TAB 1: USAGE LOGS — grouped by user, one column per feature, plus total credits */}
            {currentTab === 0 && (
                <Box className="credit-usage-card" sx={{ width: "100%", bgcolor: "var(--color-surface)", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--color-border)" }}>
                    <Table size="small">
                        <TableHead>
                            {isSuperAdmin ? (
                                <TableRow sx={{ backgroundColor: "var(--color-table-header)" }}>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>User</TableCell>
                                    <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Tenant</TableCell>
                                    <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Total Tokens</TableCell>
                                    <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Input Tokens</TableCell>
                                    <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Output Tokens</TableCell>
                                    <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Cost (USD)</TableCell>
                                </TableRow>
                            ) : (
                                <TableRow sx={{ backgroundColor: "var(--color-table-header)" }}>
                                    <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>User</TableCell>
                                    {FEATURE_COLUMNS.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            align="center"
                                            sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}
                                        >
                                            {col.label}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 700, borderBottom: '1px solid var(--color-border)' }}>
                                        Total Credits
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={isSuperAdmin ? 7 : (FEATURE_COLUMNS.length + 2)} align="center" sx={{ py: 4, color: "var(--color-muted)" }}>
                                        Loading data...
                                    </TableCell>
                                </TableRow>
                            ) : groupedUsage.length > 0 ? (
                                paginate(groupedUsage, 0).map((row) => {
                                    const tenantName = getTenantName(row.tenantId);
                                    const avatarColor = getAvatarColor(row.userId);

                                    if (isSuperAdmin) {
                                        return (
                                            <UsageRow key={row.userId} row={row} tenantName={tenantName} isSuperAdmin={isSuperAdmin} />
                                        );
                                    }

                                    return (
                                        <TableRow key={row.userId} hover sx={{ "&:hover": { backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 5%, transparent) !important" } }}>
                                            <TableCell sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <div className="flex items-center gap-2">
                                                    <Avatar
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            bgcolor: `${avatarColor}22`,
                                                            color: avatarColor,
                                                            border: `1px solid ${avatarColor}55`,
                                                        }}
                                                    >
                                                        {getInitials(row.userName)}
                                                    </Avatar>
                                                    <div>
                                                        <div className="text-sm font-medium text-[var(--color-text)] leading-tight">{row.userName}</div>
                                                        {row.userEmail && (
                                                            <div className="text-[11px] text-gray-500 leading-tight">{row.userEmail}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            {FEATURE_COLUMNS.map((col) => {
                                                const value = row.featureTotals[col.key] || 0;
                                                return (
                                                    <TableCell key={col.key} align="center" sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        {value > 0 ? (
                                                            <Tooltip title={`${col.label}: ${value} credits`}>
                                                                <span
                                                                    className="inline-flex min-w-[34px] justify-center rounded-full px-2 py-0.5 text-xs font-semibold"
                                                                    style={{
                                                                        color: "var(--color-text)",
                                                                        fontSize:"16px"
                                                                    }}
                                                                >
                                                                    {value}
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span className="text-xs text-[#4B4858]">—</span>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="right" sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <span className="inline-flex items-center gap-1  px-3 py-1 text-sm font-bold text-[var(--color-text)]">
                                                    {row.totalCredits}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={isSuperAdmin ? 7 : (FEATURE_COLUMNS.length + 2)} align="center" sx={{ py: 4, color: "var(--color-muted)", fontStyle: "italic" }}>
                                        No usage found for this period.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={groupedUsage.length}
                        page={pageByTab[0] ?? 0}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPageByTab[0] ?? 10}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{
                            color: "var(--color-muted)",
                            borderTop: "1px solid var(--color-border)",
                            "& .MuiTablePagination-actions button": { color: "var(--color-text)" },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { color: "var(--color-muted)" },
                        }}
                    />
                </Box>
            )}

            {/* TAB 2: ACTIVE BALANCES */}
            {currentTab === 1 && (
                <Box className="credit-usage-card" sx={{ width: "100%", bgcolor: "var(--color-surface)", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--color-border)" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#201E2C" }}>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Entitlements Owner</TableCell>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Monthly Plan</TableCell>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Grant Credit</TableCell>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Total Balance</TableCell>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Consumed Credit</TableCell>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Left Balance</TableCell>
                                <TableCell sx={{ color: "#888", fontWeight: 600 }}>Last Activity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredBalances.length > 0 ? (
                                paginate(filteredBalances, 1).map((bal: CreditBalance) => (
                                    <TableRow key={bal.id} hover sx={{ "&:hover": { backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 5%, transparent) !important" } }}>
                                        <TableCell sx={{ color: "var(--color-text)", borderBottom: '1px solid var(--color-border)' }}>
                                            <div className="font-medium text-[var(--color-text)]">{bal.User?.name || bal.User?.email || bal.Tenant?.name || "Unknown"}</div>
                                            <div className="text-xs text-gray-500">{bal.User ? "User" : "Tenant Organization"}</div>
                                        </TableCell>
                                        <TableCell sx={{ color: "#B8F890", fontWeight: "bold", fontSize: '1.1em', borderBottom: '1px solid #2A2A3B' }}>
                                            {bal.monthlyPlanCredits ?? 0}
                                        </TableCell>
                                        <TableCell sx={{ color: "#FFF", fontWeight: "bold", fontSize: '1.1em', borderBottom: '1px solid #2A2A3B' }}>
                                            {bal.grantedCredits ?? 0}
                                        </TableCell>
                                        <TableCell sx={{ color: "#FFF", fontWeight: "bold", fontSize: '1.1em', borderBottom: '1px solid var(--color-border)' }}>
                                            {bal.totalCreditsAllTime ?? 0}
                                        </TableCell>
                                        <TableCell sx={{ color: "#FFF", fontWeight: "bold", fontSize: '1.1em', borderBottom: '1px solid #2A2A3B' }}>
                                            {bal.consumedCredits ?? 0}
                                        </TableCell>
                                        <TableCell sx={{ color: "#FFF", fontWeight: "bold", fontSize: '1.1em', borderBottom: '1px solid #2A2A3B' }}>
                                            {bal.leftBalance ?? 0}
                                        </TableCell>
                                        <TableCell sx={{ color: "var(--color-muted)", borderBottom: '1px solid var(--color-border)' }}>{formatDateUS(bal.updatedAt, true)}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "var(--color-muted)" }}>No active balances found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredBalances.length}
                        page={pageByTab[1] ?? 0}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPageByTab[1] ?? 10}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{
                            color: "var(--color-muted)",
                            borderTop: "1px solid var(--color-border)",
                            "& .MuiTablePagination-actions button": { color: "var(--color-text)" },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { color: "var(--color-muted)" },
                        }}
                    />
                </Box>
            )}

            {/* TAB 3: GRANT HISTORY */}
            {currentTab === 2 && (
                <Box className="credit-usage-card" sx={{ width: "100%", bgcolor: "var(--color-surface)", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--color-border)" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "var(--color-table-header)" }}>
                                <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Date</TableCell>
                                <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Recipient</TableCell>
                                <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Source</TableCell>
                                <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Amount</TableCell>
                                <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Expires</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredGrants.length > 0 ? (
                                paginate(filteredGrants, 2).map((grant: CreditGrant) => {
                                    const refundAmount = refundsByGrantId.get(grant.id) || 0;
                                    const displayCredits = grant.credits + refundAmount;
                                    const refundSourceUser = refundSourceUserByGrantId.get(grant.id);
                                    const effectiveExpiresAt = grant.expiresAt || refundExpiryByGrantId.get(grant.id);
                                    const recipientName =
                                        grant.CreditBalance?.User?.name ||
                                        grant.CreditBalance?.User?.email ||
                                        grant.CreditBalance?.Tenant?.name ||
                                        "—";

                                    return (
                                        <TableRow key={grant.id} hover sx={{ "&:hover": { backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 5%, transparent) !important" } }}>
                                            <TableCell sx={{ color: "var(--color-text)", borderBottom: '1px solid var(--color-border)' }}>{formatDateUS(grant.grantedAt, true)}</TableCell>
                                            <TableCell sx={{ color: "var(--color-text)", borderBottom: '1px solid var(--color-border)' }}>
                                                <div className="font-medium text-[var(--color-text)]">{recipientName}</div>
                                                {refundSourceUser && (
                                                    <div className="text-xs text-[#9CA3AF] mt-0.5 flex items-center gap-1">
                                                        <span>Refunded from:</span>
                                                        <span className="text-[#38BDF8] font-medium">{refundSourceUser}</span>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--color-text)", borderBottom: '1px solid var(--color-border)' }}>
                                                <span className="px-2 py-1 rounded bg-[var(--color-bg)] text-xs font-mono text-[var(--color-text)] border border-[var(--color-border)]">
                                                    {grant.source === "SUPERADMIN_GRANT"
                                                        ? "Superadmin Grant"
                                                        : grant.source === "COMPANY_ADMIN_GRANT" || grant.source === "TENANT_ADMIN_GRANT"
                                                        ? "Company Admin Grant"
                                                        : grant.source === "COMPANY_ADMIN_REFUND"
                                                        ? "Company Admin Refund"
                                                        : grant.source === "ADMIN_GRANT"
                                                        ? "Admin Grant"
                                                        : grant.source?.replace(/_/g, " ") || "—"}
                                                </span>
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--color-stat-accent)", fontWeight: "bold", borderBottom: '1px solid var(--color-border)' }}>
                                                <div>+{displayCredits}</div>
                                                {refundAmount > 0 && (
                                                    <div className="text-[11px] font-normal text-[#F87171] mt-0.5">
                                                        (-{refundAmount} refunded)
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: "var(--color-muted)", borderBottom: '1px solid var(--color-border)' }}>{effectiveExpiresAt ? formatDateUS(effectiveExpiresAt) : "Never"}</TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "var(--color-muted)" }}>No grant history found for this period.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredGrants.length}
                        page={pageByTab[2] ?? 0}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPageByTab[2] ?? 10}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{
                            color: "var(--color-muted)",
                            borderTop: "1px solid var(--color-border)",
                            "& .MuiTablePagination-actions button": { color: "var(--color-text)" },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { color: "var(--color-muted)" },
                        }}
                    />
                </Box>
            )}

            {/* TAB 4: TOKEN USAGE — grouped by user, plain white token counts per feature, plus total */}
            {currentTab === 3 && (
                <Box className="credit-usage-card" sx={{ width: "100%", bgcolor: "var(--color-surface)", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--color-border)" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "var(--color-table-header)" }}>
                                <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>User</TableCell>
                                {isSuperAdmin && (
                                    <TableCell sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Tenant</TableCell>
                                )}
                                {FEATURE_COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        align="center"
                                        sx={{ color: "var(--color-text)", fontWeight: 600, borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                                <TableCell align="right" sx={{ color: "var(--color-text)", fontWeight: 700, borderBottom: '1px solid var(--color-border)' }}>
                                    Total Tokens
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={FEATURE_COLUMNS.length + (isSuperAdmin ? 3 : 2)} align="center" sx={{ py: 4, color: "var(--color-muted)" }}>
                                        Loading data...
                                    </TableCell>
                                </TableRow>
                            ) : groupedUsage.length > 0 ? (
                                paginate(groupedUsage, 3).map((row) => {
                                    const tenantName = getTenantName(row.tenantId);
                                    const avatarColor = getAvatarColor(row.userId);
                                    return (
                                        <TableRow key={row.userId} hover sx={{ "&:hover": { backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 5%, transparent) !important" } }}>
                                            <TableCell sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <div className="flex items-center gap-2">
                                                    <Avatar
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            bgcolor: `${avatarColor}22`,
                                                            color: avatarColor,
                                                            border: `1px solid ${avatarColor}55`,
                                                        }}
                                                    >
                                                        {getInitials(row.userName)}
                                                    </Avatar>
                                                    <div>
                                                        <div className="text-sm font-medium text-[var(--color-text)] leading-tight">{row.userName}</div>
                                                        {row.userEmail && (
                                                            <div className="text-[11px] text-gray-500 leading-tight">{row.userEmail}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            {isSuperAdmin && (
                                                <TableCell sx={{ color: "var(--color-muted)", borderBottom: '1px solid var(--color-border)' }}>{tenantName}</TableCell>
                                            )}
                                            {FEATURE_COLUMNS.map((col) => {
                                                const tokens = row.featureTokens[col.key] || 0;
                                                return (
                                                    <TableCell key={col.key} align="center" sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                        {tokens > 0 ? (
                                                            <Tooltip title={`${col.label}: ${tokens.toLocaleString()} tokens`}>
                                                                <span className="text-md font-bold text-[var(--color-text)]">{formatNumber(tokens)}</span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span className="text-md font-bold text-[var(--color-text)]">0</span>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="right" sx={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <Tooltip title={`${row.totalTokens.toLocaleString()} tokens`}>
                                                    <span className="text-md font-bold text-[var(--color-text)]">{formatNumber(row.totalTokens)}</span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={FEATURE_COLUMNS.length + (isSuperAdmin ? 3 : 2)} align="center" sx={{ py: 4, color: "var(--color-muted)", fontStyle: "italic" }}>
                                        No token usage found for this period.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={groupedUsage.length}
                        page={pageByTab[3] ?? 0}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPageByTab[3] ?? 10}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{
                            color: "var(--color-muted)",
                            borderTop: "1px solid var(--color-border)",
                            "& .MuiTablePagination-actions button": { color: "var(--color-text)" },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { color: "var(--color-muted)" },
                        }}
                    />
                </Box>
            )}
        </div>
    );
};

export default CreditUsageTable;